import { getMattersTableColumns } from "@/components/features/matters/MattersTable/matters-table-columns";
import { Checkbox, DataTable } from "@/components/ui";
import { formatTableColumnLabel } from "@/components/ui/shadcn/DataTable/formatTableColumnLabel";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { ReportsMatterSearchTableRow } from "@/lib/types";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

function headerLabel(label: string) {
  return (
    <span className="text-muted-foreground font-normal">
      {formatTableColumnLabel(label)}
    </span>
  );
}

function preferredEmail(matter: ReportsMatterSearchTableRow) {
  const emails =
    matter.matterContactConnection[0]?.matterContactConnectionContactEmail ??
    [];
  return (
    emails.find((email) => email.isPrimary && email.address?.trim())?.address ??
    emails.find((email) => email.address?.trim())?.address ??
    "–"
  );
}

function preferredPhone(matter: ReportsMatterSearchTableRow) {
  const phones =
    matter.matterContactConnection[0]?.matterContactConnectionContactPhone ??
    [];
  return (
    phones.find((phone) => phone.isPrimary && phone.number?.trim())?.number ??
    phones.find((phone) => phone.number?.trim())?.number ??
    "–"
  );
}

export function MassTortSurveyMatterSelectionTable({
  matters,
  selectedMatterIds,
  onToggleMatter,
  onToggleAll,
  isLoading,
  isError,
}: {
  matters: ReportsMatterSearchTableRow[];
  selectedMatterIds: Set<string>;
  onToggleMatter: (matter: ReportsMatterSearchTableRow) => void;
  onToggleAll: () => void;
  isLoading: boolean;
  isError: boolean;
}) {
  const allSelected =
    matters.length > 0 &&
    matters.every((matter) => selectedMatterIds.has(matter.matterId));
  const someSelected = matters.some((matter) =>
    selectedMatterIds.has(matter.matterId),
  );
  const columns = useMemo<ColumnDef<ReportsMatterSearchTableRow>[]>(() => {
    const matterColumns = getMattersTableColumns({ columnHeaders: "plain" });
    const coreColumnWidths: Record<string, number> = {
      Title: 250,
      Status: 135,
      "Incident Type": 150,
    };
    const coreColumns = matterColumns
      .filter((column) => column.id && column.id in coreColumnWidths)
      .map((column) => ({
        ...column,
        header: () => headerLabel(column.id ?? ""),
        size: coreColumnWidths[column.id ?? ""],
      })) as ColumnDef<ReportsMatterSearchTableRow>[];

    return [
      {
        id: "Select",
        header: () => (
          <Checkbox
            checked={
              allSelected ? true : someSelected ? "indeterminate" : false
            }
            disabled={matters.length === 0 || isLoading || isError}
            aria-label={`Select all filtered matters (${matters.length})`}
            onCheckedChange={onToggleAll}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedMatterIds.has(row.original.matterId)}
            aria-label={`Select ${row.original.matterTitle || "Untitled Matter"}`}
            onCheckedChange={() => {
              onToggleMatter(row.original);
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 45,
      },
      ...coreColumns,
      {
        id: "Client",
        accessorFn: (matter) =>
          matter.matterContactConnection[0]
            ?.matterContactConnectionContactName || "–",
        header: () => headerLabel("Client"),
        size: 145,
      },
      {
        id: "Client Email",
        accessorFn: preferredEmail,
        header: () => headerLabel("Client Email"),
        size: 220,
      },
      {
        id: "Client Phone",
        accessorFn: preferredPhone,
        header: () => headerLabel("Client Phone"),
        size: 150,
      },
    ];
  }, [
    allSelected,
    isError,
    isLoading,
    matters.length,
    onToggleAll,
    onToggleMatter,
    selectedMatterIds,
    someSelected,
  ]);

  return (
    <DataTable
      allowLocalStorage={false}
      columns={columns}
      data={matters}
      getRowId={(matter) => matter.matterId}
      hideSearchBar
      hideVisiibilityDropdown
      isLoading={isLoading}
      isError={isError}
      onRowClick={(row) => {
        onToggleMatter(row.original);
      }}
      tableName={tanstackTableNames.massTortSurveyMatterSelection}
      noResultsMessage="No matters found."
    />
  );
}
